import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { TimelinePeriodHeader } from '../../components/timeline-period-header';
import { ItemPosition, TimelineItemWithDisplay } from '../../../models/timeline-calendar.types';
import type {
  SurfaceCalendarState,
  TimelineDiffItemState,
  SurfaceEventHandlers,
  SurfaceInteractionState,
  SurfaceSectionProps,
  SurfaceLayoutState,
  SurfaceRefs,
  SurfaceSelectionState,
} from '../types';
import { ROW_HEIGHT_PX } from '../constants';
import { buildTimelineRenderFrame } from '../../webgl/timeline-frame-builder';
import { TimelineWebGLCanvas } from '../../webgl/timeline-webgl-canvas';

interface TimelineSurfaceProps {
  refs: SurfaceRefs;
  aiPreviewItemStates?: ReadonlyMap<string, TimelineDiffItemState>;
  calendar: SurfaceCalendarState;
  layout: SurfaceLayoutState;
  interaction: SurfaceInteractionState;
  events: SurfaceEventHandlers;
  sections: SurfaceSectionProps;
  selection: SurfaceSelectionState;
}

interface RenderItemViewModel {
  item: TimelineItemWithDisplay;
  position: ItemPosition;
  topPx: number;
  pointLabelSection: number;
  isPointEvent: boolean;
  isPendingItem: boolean;
  isSelected: boolean;
  isEditing: boolean;
  isResizing: boolean;
  isMoving: boolean;
  showText: boolean;
  showResizeHandles: boolean;
  rowIndex: number;
  labelMaxWidthPx: number;
  previewState: TimelineDiffItemState | undefined;
}

export const TimelineSurface = ({ refs, aiPreviewItemStates, calendar, layout, interaction, events, sections, selection }: TimelineSurfaceProps) => {
  const { zoomSurface: zoomSurfaceRef, timelineContent: timelineContentRef, timelineItems: timelineItemsRef } = refs;
  const { showPeriodHeader, periodHeaderOffset, horizontalScrollLeft, periodSegments, dateRange } = calendar;
  const { timelineMinHeight, shouldShowPreview, previewPosition, previewTop, renderPreviewAsPoint, isDurationCursorPreview, isMultiSection, multiSectionLayout, positionedRenderItems, shouldShowText, pendingItemId, pointLabelMaxWidthPx } = layout;
  const { readOnly, pendingCreateTitle, editingItemId, editingTitleDraft, setPendingCreateTitle, setEditingTitleDraft, startItemTitleEditing, handleItemClick, commitPendingCreate, commitItemTitleEditing, cancelPendingCreate, cancelItemTitleEditing, startMove, startResize, handleItemHover, isCreateFlowActive, isResizeFlowActive, isMoveFlowActive, pendingCreateDraft, resizePreviewState, movePreviewState, addMode, dragPreviewDraft } = interaction;
  const { onScroll, onTouchStart, onTouchMove, onTouchEnd, onWheel, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerEnter, onPointerLeave, onMouseMove } = events;
  const { timelines, onSectionLabelChange, onSectionDelete, onSectionMove, hiddenSectionIds, onSectionVisibilityToggle } = sections;
  const { hoveredItemId, selectedItemId, hoveredTooltip, tooltipItem, durationLabel, getRelativeFromStartLabel } = selection;
  const [surfaceSize, setSurfaceSize] = useState({ width: 1200, height: 1 });
  const [isWebGLReady, setIsWebGLReady] = useState(true);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionLabelDraft, setEditingSectionLabelDraft] = useState('');

  const cancelSectionLabelEditing = () => {
    setEditingSectionId(null);
    setEditingSectionLabelDraft('');
  };

  const startSectionLabelEditing = (sectionId: string, currentLabel: string) => {
    if (readOnly || !onSectionLabelChange) {
      return;
    }
    if (
      addMode !== 'none'
      || pendingCreateDraft
      || dragPreviewDraft
      || editingItemId
      || isResizeFlowActive
      || isMoveFlowActive
    ) {
      return;
    }
    handleItemHover(null);
    setEditingSectionId(sectionId);
    setEditingSectionLabelDraft(currentLabel);
  };

  useEffect(() => {
    if (readOnly) {
      setEditingSectionId(null);
      setEditingSectionLabelDraft('');
    }
  }, [readOnly]);

  const commitSectionLabelEditing = () => {
    if (!editingSectionId) {
      return;
    }

    const targetSection = timelines.find(( section) => section.id === editingSectionId);
    if (!targetSection) {
      cancelSectionLabelEditing();
      return;
    }

    if (targetSection.label !== editingSectionLabelDraft) {
      onSectionLabelChange?.(editingSectionId, editingSectionLabelDraft);
    }
    cancelSectionLabelEditing();
  };

  useLayoutEffect(() => {
    const target = timelineItemsRef.current;
    if (!target) {
      return;
    }

    const updateSize = () => {
      const nextWidth = Math.max(1, target.clientWidth);
      const nextHeight = Math.max(1, target.clientHeight);
      setSurfaceSize((prev) => (
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateSize();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateSize();
      });
      resizeObserver.observe(target);
    }

    window.addEventListener('resize', updateSize);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [timelineItemsRef, timelineMinHeight, positionedRenderItems.length]);

  const renderedItems = useMemo<RenderItemViewModel[]>(() => {
    let visibleIndex = 0;
    const visibleIndexBySection = new Map<string, number>();

    return positionedRenderItems
      .map(({ item, position }) => {
        if (!position.isVisible) {
          return null;
        }

        if (hiddenSectionIds.size > 0 && item.sectionId && hiddenSectionIds.has(item.sectionId)) {
          return null;
        }

        const isPendingItem = item.id === pendingItemId;
        const isSelected = !isPendingItem && selectedItemId === item.id;
        const showText = shouldShowText(position.width);
        const isResizing = resizePreviewState?.itemId === item.id;
        const isMoving = movePreviewState?.itemId === item.id;
        const sectionId = item.sectionId ?? '';
        const showResizeHandles = !isPendingItem
          && !readOnly
          && addMode === 'none'
          && !pendingCreateDraft
          && !dragPreviewDraft
          && !editingItemId
          && !isMoveFlowActive
          && (hoveredItemId === item.id || isResizing);

        const currentVisibleIndex = isMultiSection
          ? (visibleIndexBySection.get(sectionId) ?? 0)
          : visibleIndex++;
        if (isMultiSection) {
          visibleIndexBySection.set(sectionId, currentVisibleIndex + 1);
        }

        const sectionOffsetRow = isMultiSection
          ? (multiSectionLayout.sectionOffsetRows.get(sectionId) ?? 1)
          : 0;
        const rowIndex = isMultiSection
          ? sectionOffsetRow + currentVisibleIndex
          : currentVisibleIndex;
        const pointLabelSection = 0;

        const isPointEvent = !item.endDate;
        const topPx = rowIndex * ROW_HEIGHT_PX + 10;
        const previewState = aiPreviewItemStates?.get(item.id);

        const LABEL_MARGIN_PX = 10;
        const leftPct = parseFloat(position.left) || 0;
        const widthPct = isPointEvent ? 0 : (parseFloat(position.width) || 0);
        const rightEdgePx = isPointEvent
          ? (leftPct / 100) * surfaceSize.width - 6 + 12
          : ((leftPct + widthPct) / 100) * surfaceSize.width;
        const remainingPx = surfaceSize.width - rightEdgePx - LABEL_MARGIN_PX;
        const labelMaxWidthPx = remainingPx > 0 ? remainingPx : 0;

        return {
          item,
          position,
          topPx,
          pointLabelSection,
          isPointEvent,
          isPendingItem,
          isSelected,
          isEditing: isPendingItem || editingItemId === item.id,
          isResizing,
          isMoving,
          showText,
          showResizeHandles,
          rowIndex,
          labelMaxWidthPx,
          previewState,
        } satisfies RenderItemViewModel;
      })
      .filter((item): item is RenderItemViewModel => item !== null);
  }, [
    aiPreviewItemStates,
    positionedRenderItems,
    pendingItemId,
    selectedItemId,
    shouldShowText,
    resizePreviewState?.itemId,
    movePreviewState?.itemId,
    addMode,
    pendingCreateDraft,
    dragPreviewDraft,
    editingItemId,
    isMoveFlowActive,
    readOnly,
    hoveredItemId,
    isMultiSection,
    multiSectionLayout.sectionOffsetRows,
    surfaceSize.width,
    hiddenSectionIds,
  ]);

  const renderFrame = useMemo(() => buildTimelineRenderFrame({
    width: surfaceSize.width,
    height: surfaceSize.height,
    dpr: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
    periodSegments,
    dateRange,
    hoveredItemId,
    selectedItemId,
    editingItemId,
    movePreviewItemId: movePreviewState?.itemId ?? null,
    resizePreviewItemId: resizePreviewState?.itemId ?? null,
    renderedItems: renderedItems.map((entry) => ({
      item: entry.item,
      row: entry.rowIndex,
      pointLabelSection: entry.pointLabelSection,
      position: entry.position,
      isPending: entry.isPendingItem,
      showResizeHandles: entry.showResizeHandles,
      previewState: entry.previewState,
    })),
    gridLinesY: isMultiSection
      ? timelines.slice(1).map(( section) => {
          const offsetRow = multiSectionLayout.sectionOffsetRows.get(section.id) ?? 0;
          return offsetRow * ROW_HEIGHT_PX;
        })
      : [],
  }), [
    surfaceSize.width,
    surfaceSize.height,
    periodSegments,
    dateRange,
    hoveredItemId,
    selectedItemId,
    editingItemId,
    movePreviewState?.itemId,
    resizePreviewState?.itemId,
    renderedItems,
    isMultiSection,
    timelines,
    multiSectionLayout.sectionOffsetRows,
  ]);

  const tooltipSize = { width: 280, height: 140 };
  const tooltipOffset = 10;
  const tooltipPosition = hoveredTooltip
    ? {
      left: Math.max(
        tooltipOffset,
        Math.min(
          hoveredTooltip.clientX + tooltipOffset,
          window.innerWidth - tooltipSize.width - tooltipOffset,
        ),
      ),
      top: Math.max(
        tooltipOffset,
        Math.min(
          hoveredTooltip.clientY + tooltipOffset,
          window.innerHeight - tooltipSize.height - tooltipOffset,
        ),
      ),
    }
    : null;

  return (
    <>
      {showPeriodHeader && (
        <div
          className="sticky z-20 overflow-hidden"
          data-testid="timeline-period-header-sticky-wrapper"
          style={{
            top: `${periodHeaderOffset}px`,
            backgroundColor: 'var(--color-bg-app)',
          }}
        >
          <div
            className="min-w-[1200px] px-4"
            style={{ transform: `translateX(-${horizontalScrollLeft}px)` }}
          >
            <TimelinePeriodHeader
              periodSegments={periodSegments}
              sticky={false}
            />
          </div>
        </div>
      )}

      <div
        ref={zoomSurfaceRef}
        className="isolate flex min-h-0 w-full flex-1 flex-col overflow-x-auto"
        data-testid="timeline-zoom-surface"
        onScroll={isWebGLReady ? onScroll : undefined}
        onTouchStart={isWebGLReady ? onTouchStart : undefined}
        onTouchMove={isWebGLReady ? onTouchMove : undefined}
        onTouchEnd={isWebGLReady ? onTouchEnd : undefined}
        onTouchCancel={isWebGLReady ? onTouchEnd : undefined}
        onWheel={isWebGLReady ? onWheel : undefined}
        onPointerDown={isWebGLReady ? onPointerDown : undefined}
        onPointerMove={isWebGLReady ? onPointerMove : undefined}
        onPointerUp={isWebGLReady ? onPointerUp : undefined}
        onPointerCancel={isWebGLReady ? onPointerCancel : undefined}
        onPointerEnter={isWebGLReady ? onPointerEnter : undefined}
        onPointerLeave={isWebGLReady ? onPointerLeave : undefined}
        onMouseMove={isWebGLReady ? onMouseMove : undefined}
        style={{
          touchAction: 'none',
          cursor: !isWebGLReady
            ? 'not-allowed'
            : addMode === 'none'
            ? (isMoveFlowActive ? 'grabbing' : 'default')
            : 'crosshair',
        }}
      >
        <div ref={timelineContentRef} className="flex min-w-[1200px] flex-1 flex-col p-4">
          <div
            ref={timelineItemsRef}
            data-testid="timeline-items-area"
            className="relative flex-1"
            style={{ minHeight: timelineMinHeight }}
          >
            <TimelineWebGLCanvas
              frame={renderFrame}
              className="pointer-events-none absolute inset-0 z-0"
              onReadyChange={setIsWebGLReady}
            />

            {shouldShowPreview && previewPosition && (
              <div
                data-testid={dragPreviewDraft ? 'timeline-create-preview-drag' : 'timeline-create-preview-cursor'}
                className="pointer-events-none absolute z-25"
                style={{
                  top: `${previewTop}px`,
                  left: renderPreviewAsPoint ? `calc(${previewPosition.left} - 6px)` : previewPosition.left,
                  width: renderPreviewAsPoint ? '12px' : (isDurationCursorPreview ? '28px' : previewPosition.width),
                  height: renderPreviewAsPoint ? '12px' : '14px',
                  border: '2px dashed var(--color-accent)',
                  borderRadius: renderPreviewAsPoint ? '9999px' : 'var(--radius-md)',
                  backgroundColor: 'var(--color-accent-soft)',
                  opacity: dragPreviewDraft ? 1 : 0.7,
                }}
              />
            )}

            {isMultiSection && (
              <div className="pointer-events-none absolute inset-0 z-30">
                {timelines.map((section, sectionIndex) => {
                  const sectionOffsetRow = multiSectionLayout.sectionOffsetRows.get(section.id) ?? 0;
                  const labelTop = sectionOffsetRow * ROW_HEIGHT_PX;
                  const isEditingSectionLabel = editingSectionId === section.id;
                  return (
                    isEditingSectionLabel ? (
                      <input
                        key={`timeline-section-label-${section.id}`}
                        type="text"
                        data-testid={`timeline-section-label-input-${section.id}`}
                        data-timeline-section-anchor="true"
                        data-timeline-section-id={section.id}
                        className="pointer-events-auto absolute left-0 rounded-sm px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          top: `${labelTop}px`,
                          minWidth: '140px',
                          color: 'var(--color-text-primary)',
                          backgroundColor: 'var(--color-bg-input)',
                          border: '1px solid var(--color-border-focus)',
                          outline: 'none',
                          boxShadow: '0 0 0 2px rgba(12, 140, 233, 0.2)',
                        }}
                        value={editingSectionLabelDraft}
                        autoFocus
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        onPointerDown={(event) => {
                          event.stopPropagation();
                        }}
                        onChange={(event) => {
                          setEditingSectionLabelDraft(event.target.value);
                        }}
                        onBlur={() => {
                          commitSectionLabelEditing();
                        }}
                        onKeyDown={(event) => {
                          if (event.nativeEvent.isComposing) return;
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.blur();
                            return;
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            cancelSectionLabelEditing();
                          }
                        }}
                      />
                    ) : (
                      <div
                        key={`timeline-section-label-${section.id}`}
                        data-testid={`timeline-section-label-${section.id}`}
                        data-timeline-section-anchor="true"
                        data-timeline-section-id={section.id}
                        className="pointer-events-auto absolute left-0 inline-flex items-center gap-0.5 rounded-sm text-[11px] font-medium transition-colors group"
                        style={{
                          top: `${labelTop}px`,
                          color: 'var(--color-text-secondary)',
                          backgroundColor: 'color-mix(in srgb, var(--color-bg-surface) 80%, transparent)',
                          backdropFilter: 'blur(4px)',
                          border: '1px solid color-mix(in srgb, var(--color-border) 50%, transparent)',
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onPointerDown={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {!readOnly && onSectionVisibilityToggle && (
                          <button
                            type="button"
                            className="flex items-center justify-center px-1 py-0.5 rounded hover:bg-black/10"
                            title={hiddenSectionIds.has(section.id) ? 'アイテムを表示' : 'アイテムを非表示'}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onSectionVisibilityToggle(section.id);
                            }}
                          >
                            {hiddenSectionIds.has(section.id) ? (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 2L12 12" />
                                <path d="M5.6 5.7A1.8 1.8 0 0 0 8.3 8.4" />
                                <path d="M3.5 4.2C2.3 5.1 1.5 6.3 1.2 7c.7 1.7 2.8 4.2 5.8 4.2.8 0 1.6-.2 2.2-.4" />
                                <path d="M10 10.2c1.3-.9 2.2-2.2 2.8-3.2C12 5.3 10 2.8 7 2.8c-.6 0-1.2.1-1.7.3" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1.2 7c.7-1.7 2.8-4.2 5.8-4.2s5.1 2.5 5.8 4.2c-.7 1.7-2.8 4.2-5.8 4.2S1.9 8.7 1.2 7z" />
                                <circle cx="7" cy="7" r="1.8" />
                              </svg>
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className="px-2 py-0.5"
                          style={{
                            opacity: hiddenSectionIds.has(section.id) ? 0.5 : 1,
                          }}
                          onDoubleClick={(event) => {
                            if (!isWebGLReady || readOnly || !onSectionLabelChange) {
                              return;
                            }
                            event.preventDefault();
                            event.stopPropagation();
                            startSectionLabelEditing(section.id, section.label);
                          }}
                        >
                          {section.label}
                        </button>
                        {hiddenSectionIds.has(section.id) && (() => {
                          const count = positionedRenderItems.filter(({ item }) => item.sectionId === section.id).length;
                          return count > 0 ? (
                            <span
                              className="py-0.5 pr-1 text-[10px]"
                              style={{ color: 'var(--color-text-tertiary)' }}
                            >
                              {count}件
                            </span>
                          ) : null;
                        })()}
                        {!readOnly && (
                          <div className="hidden items-center gap-0 pr-0.5 group-hover:inline-flex">
                            {sectionIndex > 0 && onSectionMove && (
                              <button
                                type="button"
                                data-testid={`timeline-section-move-up-${section.id}`}
                                className="rounded p-0.5 hover:bg-black/10"
                                title="上に移動"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onSectionMove(section.id, sectionIndex - 1);
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M6 9V3M3.5 5.5L6 3L8.5 5.5" />
                                </svg>
                              </button>
                            )}
                            {sectionIndex < timelines.length - 1 && onSectionMove && (
                              <button
                                type="button"
                                data-testid={`timeline-section-move-down-${section.id}`}
                                className="rounded p-0.5 hover:bg-black/10"
                                title="下に移動"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onSectionMove(section.id, sectionIndex + 1);
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M6 3V9M3.5 6.5L6 9L8.5 6.5" />
                                </svg>
                              </button>
                            )}
                            {timelines.length > 1 && onSectionDelete && (
                              <button
                                type="button"
                                data-testid={`timeline-section-delete-${section.id}`}
                                className="rounded p-0.5 hover:bg-red-500/20"
                                style={{ color: 'var(--color-text-tertiary)' }}
                                title="セクションを削除"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onSectionDelete(section.id);
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M2.5 3.5h7M4.5 3.5V2.5a1 1 0 011-1h1a1 1 0 011 1v1M5 5.5v3M7 5.5v3M3.5 3.5l.5 6a1 1 0 001 1h2a1 1 0 001-1l.5-6" />
                                </svg>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  );
                })}
              </div>
            )}

            <div className="absolute inset-0 z-20">
              {renderedItems.map((entry) => {
                const {
                  item,
                  position,
                  topPx,
                  isPointEvent,
                  isPendingItem,
                  isSelected,
                  isEditing,
                  showText,
                  showResizeHandles,
                  isResizing,
                  isMoving,
                  labelMaxWidthPx,
                  previewState,
                } = entry;
                const isPreviewAdded = previewState === 'added';
                const isPreviewExisting = previewState === 'existing';

                return (
                  <div
                    key={item.id}
                    data-testid={`timeline-item-${item.id}`}
                    data-item-title={item.title}
                    data-timeline-item-root="true"
                    data-preview-state={previewState ?? 'default'}
                    className="absolute"
                    style={{
                      top: `${topPx}px`,
                      height: isPointEvent ? '12px' : '14px',
                      left: isPointEvent ? `calc(${position.left} - 6px)` : position.left,
                      width: isPointEvent ? '12px' : position.width,
                      zIndex: isSelected ? 10 : (isPreviewAdded ? 2 : 1),
                      cursor: readOnly || isPendingItem || addMode !== 'none' || pendingCreateDraft
                        ? 'default'
                        : (isMoving ? 'grabbing' : 'grab'),
                      borderRadius: isPointEvent ? '9999px' : '6px',
                      backgroundColor: 'transparent',
                      opacity: isPreviewExisting ? 0.68 : 1,
                      boxShadow: isPreviewAdded
                        ? '0 0 0 1px rgba(12, 140, 233, 0.9), 0 0 18px rgba(12, 140, 233, 0.18)'
                        : 'none',
                      transition: 'opacity 0.18s ease, box-shadow 0.18s ease',
                    }}
                    onClick={() => {
                      if (!isWebGLReady) {
                        return;
                      }
                      if (isPendingItem) {
                        return;
                      }
                      handleItemClick(item.id);
                    }}
                    onDoubleClick={() => {
                      if (!isWebGLReady || readOnly) {
                        return;
                      }
                      if (isPendingItem) {
                        return;
                      }
                      startItemTitleEditing(item.id, item.title);
                    }}
                    onPointerDown={(event) => {
                      if (!isWebGLReady) {
                        return;
                      }
                      if (event.pointerType === 'touch' || event.button === 0) {
                        handleItemClick(item.id);
                      }
                      if (readOnly || isPendingItem || addMode !== 'none' || pendingCreateDraft) {
                        return;
                      }
                      startMove(item.id, event);
                    }}
                    onMouseEnter={(event) => {
                      if (!isWebGLReady) {
                        return;
                      }
                      if (
                        editingItemId
                        || pendingCreateDraft
                        || dragPreviewDraft
                        || isResizeFlowActive
                        || isMoveFlowActive
                      ) {
                        handleItemHover(null);
                        return;
                      }
                      handleItemHover({
                        itemId: item.id,
                        clientX: event.clientX,
                        clientY: event.clientY,
                      });
                    }}
                    onMouseMove={(event) => {
                      if (!isWebGLReady) {
                        return;
                      }
                      if (
                        editingItemId
                        || pendingCreateDraft
                        || dragPreviewDraft
                        || isResizeFlowActive
                        || isMoveFlowActive
                      ) {
                        handleItemHover(null);
                        return;
                      }
                      handleItemHover({
                        itemId: item.id,
                        clientX: event.clientX,
                        clientY: event.clientY,
                      });
                    }}
                    onMouseLeave={() => {
                      if (!isWebGLReady) {
                        return;
                      }
                      handleItemHover(null);
                    }}
                  >

                    {showResizeHandles && (
                      <div
                        data-testid={`timeline-item-resize-handle-start-${item.id}`}
                        className="absolute top-1/2 z-20 h-6 w-3 -translate-y-1/2"
                        style={{
                          left: 0,
                          transform: 'translate(-50%, -50%)',
                          cursor: 'ew-resize',
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onPointerDown={(event) => {
                          if (!isWebGLReady) {
                            return;
                          }
                          event.preventDefault();
                          event.stopPropagation();
                          startResize(item.id, 'start', event);
                        }}
                      >
                        <div
                          className="mx-auto h-full w-[2px] rounded-full"
                          style={{
                            backgroundColor: isResizing && resizePreviewState?.edge === 'start'
                              ? 'var(--color-accent)'
                              : 'rgba(255,255,255,0.85)',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                          }}
                        />
                      </div>
                    )}

                    {showResizeHandles && (
                      <div
                        data-testid={`timeline-item-resize-handle-end-${item.id}`}
                        className="absolute top-1/2 z-20 h-6 w-3 -translate-y-1/2"
                        style={{
                          right: 0,
                          transform: 'translate(50%, -50%)',
                          cursor: 'ew-resize',
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onPointerDown={(event) => {
                          if (!isWebGLReady) {
                            return;
                          }
                          event.preventDefault();
                          event.stopPropagation();
                          startResize(item.id, 'end', event);
                        }}
                      >
                        <div
                          className="mx-auto h-full w-[2px] rounded-full"
                          style={{
                            backgroundColor: isResizing && resizePreviewState?.edge === 'end'
                              ? 'var(--color-accent)'
                              : 'rgba(255,255,255,0.85)',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                          }}
                        />
                      </div>
                    )}

                    {isEditing ? (
                      <input
                        type="text"
                        data-testid={`timeline-item-title-input-${item.id}`}
                        className="absolute rounded-sm px-2 py-1 text-xs font-medium"
                        style={{
                          color: 'var(--color-text-primary)',
                          backgroundColor: 'var(--color-bg-input)',
                          border: '1px solid var(--color-border-focus)',
                          boxShadow: '0 0 0 2px rgba(12, 140, 233, 0.2)',
                          minWidth: '200px',
                          outline: 'none',
                          left: 'calc(100% + 10px)',
                          top: isPointEvent ? '-8px' : '2px',
                        }}
                        value={isPendingItem
                          ? pendingCreateTitle
                          : (editingItemId === item.id ? editingTitleDraft : item.title)}
                        autoFocus
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        onPointerDown={(event) => {
                          event.stopPropagation();
                        }}
                        onChange={(event) => {
                          if (isPendingItem) {
                            setPendingCreateTitle(event.target.value);
                            return;
                          }
                          setEditingTitleDraft(event.target.value);
                        }}
                        onBlur={() => {
                          if (isPendingItem) {
                            commitPendingCreate();
                            return;
                          }
                          commitItemTitleEditing();
                        }}
                        onKeyDown={(event) => {
                          if (event.nativeEvent.isComposing) return;
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.blur();
                            return;
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            if (isPendingItem) {
                              cancelPendingCreate();
                              return;
                            }
                            cancelItemTitleEditing();
                          }
                        }}
                      />
                    ) : (
                      <span
                        data-testid={`timeline-item-label-${item.id}`}
                        className="absolute ml-2.5 items-center gap-1 whitespace-nowrap text-[11px]"
                        style={{
                          left: '100%',
                          top: isPointEvent ? '0px' : '0px',
                          color: isSelected || isPreviewAdded
                            ? 'var(--color-text-primary)'
                            : (isPreviewExisting ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)'),
                          fontWeight: isSelected || isPreviewAdded ? 600 : 400,
                          maxWidth: labelMaxWidthPx !== undefined
                            ? `${Math.min(labelMaxWidthPx, isPointEvent ? pointLabelMaxWidthPx : Infinity)}px`
                            : (isPointEvent ? `${pointLabelMaxWidthPx}px` : undefined),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-flex',
                          alignItems: 'center',
                          opacity: isPreviewExisting ? 0.62 : 1,
                          pointerEvents: 'auto',
                        }}
                        onClick={(event) => {
                          if (!isWebGLReady) {
                            return;
                          }
                          event.stopPropagation();
                          if (!isPendingItem) {
                            handleItemClick(item.id);
                          }
                        }}
                      >
                        {isPreviewAdded ? (
                          <span
                            data-testid={`timeline-item-added-badge-${item.id}`}
                            className="shrink-0 rounded-full px-1.5 py-px text-[9px] font-semibold tracking-[0.08em]"
                            style={{
                              backgroundColor: 'color-mix(in srgb, var(--color-accent) 18%, transparent)',
                              color: 'var(--color-accent)',
                              border: '1px solid color-mix(in srgb, var(--color-accent) 28%, transparent)',
                            }}
                          >
                            追加
                          </span>
                        ) : null}
                        <span
                          style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.title}
                        </span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

          </div>

          {!isCreateFlowActive && !isResizeFlowActive && !isMoveFlowActive && !editingItemId && hoveredTooltip && tooltipPosition && tooltipItem && (
            <div
              className="rounded-md"
              style={{
                position: 'fixed',
                left: `${tooltipPosition.left}px`,
                top: `${tooltipPosition.top}px`,
                width: `${tooltipSize.width}px`,
                backgroundColor: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border-strong)',
                boxShadow: 'var(--shadow-float)',
                zIndex: 9999,
                pointerEvents: 'none',
                overflow: 'hidden',
                opacity: 1,
              }}
            >
              <div className="px-3 py-2.5">
                <p className="mb-0.5 truncate text-[10px]" style={{ color: 'var(--color-text-tertiary)' }}>
                  {tooltipItem.sectionLabel}
                </p>
                <h3
                  className="mb-1 truncate text-xs font-semibold"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {tooltipItem.title}
                </h3>
                <div className="flex flex-col gap-0.5">
                  <p className="text-[10px]" style={{ color: 'var(--color-text-secondary)' }}>
                    {(() => {
                      const startDate = new Date(tooltipItem.startDate);
                      const hasStartTime = startDate.getHours() !== 0 || startDate.getMinutes() !== 0;
                      return hasStartTime
                        ? format(startDate, 'yyyy年MM月dd日 HH:mm', { locale: ja })
                        : format(startDate, 'yyyy年MM月dd日', { locale: ja });
                    })()}
                    {tooltipItem.endDate && (
                      <> 〜 {(() => {
                        const endDate = new Date(tooltipItem.endDate);
                        const hasEndTime = endDate.getHours() !== 0 || endDate.getMinutes() !== 0;
                        return hasEndTime
                          ? format(endDate, 'yyyy年MM月dd日 HH:mm', { locale: ja })
                          : format(endDate, 'yyyy年MM月dd日', { locale: ja });
                      })()}</>
                    )}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--color-text-tertiary)' }}>
                    {getRelativeFromStartLabel(tooltipItem.startDate)}
                    {durationLabel && <> / {durationLabel}</>}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
